import { Link } from 'react-router-dom'
import PolicyPage, { PolicySection, PolicyList } from 'components/compliance/policy-page'
import compliance from 'data/compliance.json'

const { organisation, contact, privacy } = compliance

function PrivacyPolicy() {
  return (
    <PolicyPage
      title='Privacy Policy'
      description='How UHDSS collects, uses, shares and protects the personal information of students, applicants and website visitors.'
      intro='This notice explains what personal information we collect through this website and our services, why we use it, who we share it with, and the choices you have.'
    >
      <PolicySection title='Who we are'>
        <p className='dark-blue fs-6'>
          {organisation.legalName}, referred to here as UHDSS, is responsible for the personal
          information described in this notice.
        </p>
        <ul className='dark-blue fs-6'>
          <li className='mb-2'>Address: {contact.headOffice.join(', ')}</li>
          <li className='mb-2'>
            Email:{' '}
            <a className='text-decoration-none' href={`mailto:${contact.generalEmail}`}>{contact.generalEmail}</a>
          </li>
          <li className='mb-2'>
            Telephone:{' '}
            <a className='text-decoration-none' href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>
          </li>
          <li className='mb-2'>Website: {organisation.website}</li>
        </ul>
        <p className='dark-blue fs-6'>Effective date: {privacy.effectiveDate}</p>
      </PolicySection>

      <PolicySection title='Information we collect on this website'>
        <p className='dark-blue fs-6'>
          We only collect what you give us. What we hold depends on which part of the site you use.
        </p>

        <h3 className='h6 dark-blue mt-4'>When you create an account or sign in</h3>
        <PolicyList items={[
          'Your name, email address and the password you choose.',
          'A sign-in token, which is stored in your own browser so that you stay signed in.'
        ]} />

        <h3 className='h6 dark-blue mt-4'>When you apply to study abroad</h3>
        <PolicyList items={[
          'Your name, gender, nationality, marital or residence status and contact numbers, including a WhatsApp number where you give one.',
          'The country, institution and level of study you are interested in, and your education background.',
          'Documents you upload, which may include your passport, transcripts, diplomas, English language results and letters of recommendation.'
        ]} />

        <h3 className='h6 dark-blue mt-4'>When you apply for a visa through us</h3>
        <PolicyList items={[
          'Your name, email address, address, the country you are in and the country you are interested in, and the type of visa.',
          'Documents you upload, which may include your passport, a photograph, evidence of financial support, an admission letter, an invitation letter and evidence of the purpose of travel.'
        ]} />

        <h3 className='h6 dark-blue mt-4'>When you ask to become an agent</h3>
        <PolicyList items={[
          'Your agency name, email address, telephone number, country and city.'
        ]} />

        <h3 className='h6 dark-blue mt-4'>When you track an application with a Unique ID</h3>
        <PolicyList items={[
          'The Unique ID you type in, which we use to show you the record we hold for your application. That record can include your name, email address, telephone number, address, the services you asked for, updates on your case, the staff assisting you, and documents.',
          'Any comment you send to us from that page.'
        ]} />

        <h3 className='h6 dark-blue mt-4'>When you contact us</h3>
        <PolicyList items={[
          'The enquiry form on our home page opens a message in your own email application. It does not store anything on this website. We receive your name, email address and message only when you send that email to us.',
          'If you write to us, call us or visit an office, we keep a record of the contact and of what we agreed.'
        ]} />

        <p className='dark-blue fs-6'>
          Some information is sensitive, for example medical evidence or details of an immigration
          history. We ask for it only where an institution or an authority requires it for your
          application, and we handle it with particular care.
        </p>
        <p className='dark-blue fs-6'>
          We may also receive information about you from a parent, a sponsor, a referee, an agent or
          an institution, where they are involved in your application.
        </p>
      </PolicySection>

      <PolicySection title='Why we use your information'>
        <PolicyList items={[
          'To give you counselling and to assess whether a programme or destination is suitable for you.',
          'To prepare, submit and manage your applications where you have asked us to.',
          'To support visa preparation and the administrative steps around it.',
          'To communicate with you, with institutions and with the service providers involved in your case.',
          'To verify the information and documents we are given, and to reduce fraud and misuse of our services.',
          'To manage appointments, records, complaints and the quality of our service.',
          'To meet our legal and regulatory obligations, and to protect applicants who may be at risk.',
          'To keep this website working properly and securely.'
        ]} />
        <p className='dark-blue fs-6'>
          We do not use your information for advertising, profiling or automated decision making,
          and we do not sell it.
        </p>
      </PolicySection>

      <PolicySection title='The lawful bases we rely on'>
        <p className='dark-blue fs-6'>
          Depending on your situation and on the data protection law that applies to you, we rely on
          one or more of the following:
        </p>
        <PolicyList items={[
          'It is necessary to provide the service you asked for, or to take steps before we agree to work together.',
          'We or a third party have a legitimate interest that does not override your rights, for example keeping proper records of applications and complaints.',
          'We have to process the information to meet a legal obligation.',
          'You have given your consent, which you can withdraw at any time.',
          'In an urgent situation, to protect someone.'
        ]} />
        <p className='dark-blue fs-6'>
          Where we handle sensitive information, we do so only with an additional lawful condition,
          which in most cases will be your explicit consent to support a specific application.
        </p>
      </PolicySection>

      <PolicySection title='Who we share your information with'>
        <p className='dark-blue fs-6'>
          We share your information only where it is necessary for the service you asked for, or
          where the law requires it. This may include:
        </p>
        <PolicyList items={[
          'Universities, colleges, schools and other education providers you apply to.',
          'Embassies, consulates, immigration authorities and visa application centres, where this is authorised or required.',
          'Testing organisations, credential evaluators and scholarship bodies.',
          'Accommodation, travel, insurance, translation and courier providers, where you ask us to arrange something.',
          'Professional advisers, auditors and regulators.',
          'Law enforcement or public authorities, where disclosure is lawful and required.',
          'A parent, sponsor or representative you have authorised.'
        ]} />
        <p className='dark-blue fs-6'>
          We also use technology providers who process information on our behalf so that our
          services can run:
        </p>
        <ul className='dark-blue fs-6'>
          {privacy.processors.map((processor, index) => (
            <li className='mb-2' key={index}>
              <strong>{processor.name}</strong> — {processor.purpose}
            </li>
          ))}
        </ul>
        <p className='dark-blue fs-6'>
          These providers receive only the information they need for their role, and they are
          required to protect it.
        </p>
      </PolicySection>

      <PolicySection title='Where your information is held'>
        <p className='dark-blue fs-6'>
          Applying to study abroad means that your information usually has to be sent outside the
          country you live in, to the institution or authority that will assess your application.
          Some of the technology providers listed above also store information outside your country.
          We take reasonable steps to use lawful and appropriate safeguards for these transfers
          where the law requires them.
        </p>
      </PolicySection>

      <PolicySection title='How long we keep it'>
        {privacy.retentionPeriod.value ? (
          <p className='dark-blue fs-6'>
            We keep student records and the documents you upload for {privacy.retentionPeriod.value},
            counted from the end of the service we provided to you. We may keep a record for longer
            where an application is still in progress, or where the law requires it, for example a
            record of a complaint.
          </p>
        ) : (
          <p className='dark-blue fs-6'>
            We keep personal information only for as long as we reasonably need it: to provide the
            service, to keep proper records of applications and complaints, and to meet legal,
            contractual or regulatory requirements.
          </p>
        )}
        <p className='dark-blue fs-6'>
          You can ask us what we hold about you at any time, and ask us to delete records we no
          longer need.
        </p>
      </PolicySection>

      <PolicySection title='Keeping your information safe'>
        <p className='dark-blue fs-6'>
          Your connection to this website is encrypted, accounts are protected by a password, and
          access to student records is limited to staff who need it. Uploaded documents are held by
          the storage provider listed above. We use reasonable organisational and technical measures
          to protect information against unauthorised access, loss or disclosure, but no website or
          system can be completely secure.
        </p>
        <p className='dark-blue fs-6'>
          Please be careful with anyone who contacts you claiming to be UHDSS. We will not ask you
          to pay tuition, visa or government fees into a personal account. See our{' '}
          <Link className='text-decoration-none' to='/payments'>tuition and student payments policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title='Cookies and similar technologies'>
        <PolicyList items={[
          'This website does not use advertising cookies, and we do not run analytics or tracking scripts of our own.',
          'When you sign in, we store a sign-in token in your browser so that your session continues. Without it you would have to sign in again on every page. It stays on your own device and is removed when you sign out.',
          'Our Accommodations page loads a listings widget from a third party, and images and video are loaded from our media provider. These providers receive the technical information that any web request involves, such as your device type and network address, and they may set their own cookies on that page.'
        ]} />
        <p className='dark-blue fs-6'>
          If we later add analytics or other non essential cookies, we will update this notice and
          ask for your consent where the law requires it.
        </p>
      </PolicySection>

      <PolicySection title='Your rights'>
        <p className='dark-blue fs-6'>
          Depending on the law that applies to you, you may have the right to ask us to give you a
          copy of your information, to correct it if it is wrong, to delete it, to restrict or
          object to how we use it, to receive it in a portable form, and to withdraw consent you
          have given. These rights have limits, and we may not be able to delete information that we
          need to keep for legal reasons or for an application that is still in progress.
        </p>
        <p className='dark-blue fs-6'>
          To make a request, write to{' '}
          <a className='text-decoration-none' href={`mailto:${contact.generalEmail}?subject=Data protection request`}>{contact.generalEmail}</a>.
          We may need to check who you are before we answer, and we will respond as quickly as we
          reasonably can.
        </p>
      </PolicySection>

      <PolicySection title='If you are not happy with how we handle your information'>
        <p className='dark-blue fs-6'>
          Please tell us first, through our{' '}
          <Link className='text-decoration-none' to='/complaints'>complaints policy</Link>, so that
          we have a chance to put it right.{' '}
          {privacy.dataProtectionAuthority.value
            ? `You also have the right to complain to the data protection authority that supervises us, which is ${privacy.dataProtectionAuthority.value}. If you live in another country, you may be able to complain to the authority there instead.`
            : 'You also have the right to complain to the data protection authority in your country. If you ask us, we will tell you which authority applies to the way we have handled your information.'}
        </p>
      </PolicySection>

      <PolicySection title='Changes to this notice'>
        <p className='dark-blue fs-6'>
          We update this notice when our services, our systems or the law change. The date at the
          top of the page shows when it was last updated, and we will tell you about important
          changes where it is appropriate to do so.
        </p>
      </PolicySection>
    </PolicyPage>
  )
}

export default PrivacyPolicy
